"use client";

import { Check, ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useStore } from "@/contexts/StoreContext";
import { useCountrySwitch } from "@/hooks/useCountrySwitch";
import { getDomainMarkets } from "@/lib/domain/host-map";

export function CountrySwitcher() {
  const { country, currency, loading } = useStore();
  const tc = useTranslations("common");
  const { regionHref } = useCountrySwitch();
  const regions = getDomainMarkets();

  // Single (or no) configured domain — nothing to switch between (e.g. local dev).
  if (regions.length <= 1) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // The pinned country is unique per region (incl. the RoW `ww` sentinel), so it
  // identifies the current region — its explicit flag/label drive the trigger.
  const current = regions.find((r) => r.country === country.toLowerCase());

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost">
          <span className="text-lg leading-none">{current?.flag ?? "🌐"}</span>
          <span className="font-medium">{current?.label ?? "Region"}</span>
          <span className="text-gray-400">|</span>
          <span>{currency}</span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{tc("selectCountry")}</DropdownMenuLabel>
        {regions.map((region) => {
          const isSelected = region.country === country.toLowerCase();
          const href = isSelected ? null : regionHref(region);
          const content = (
            <>
              <span className="text-lg leading-none">{region.flag}</span>
              <span className="flex-1 font-medium">{region.label}</span>
              <span className="text-xs text-muted-foreground">
                {region.currency}
              </span>
              {isSelected && <Check className="w-4 h-4" />}
            </>
          );
          // A real link, so Google's cross-domain linker carries the session over.
          return href ? (
            <DropdownMenuItem key={region.host} asChild>
              <a href={href}>{content}</a>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem key={region.host} disabled>
              {content}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
